import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import fuzzaldrin from 'fuzzaldrin-plus'
import Downshift from 'downshift'
import VirtualList from 'react-tiny-virtual-list'
import {
  AutocompleteItem,
  Popover,
  Position,
  Heading,
  Pane,
  majorScale
} from 'evergreen-v4'

const GROUP_ITEM_TYPE = Symbol('groupItemType')

const fuzzyFilter = (items, input) => {
  return fuzzaldrin.filter(items, input, { key: 'label' })
}

const autocompleteItemRenderer = props => <AutocompleteItem {...props} />

// https://github.com/paypal/downshift/issues/164
export default class Autocomplete extends PureComponent {
  static propTypes = {
    /**
     * This prop can be either a string or a Node.
     * It will provide a title for the items
     */
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

    /**
     * An array of items to be used as options for the select
     */
    items: PropTypes.array.isRequired,

    /**
     * The selected Item to be shown on the autocomplete
     */
    selectedItem: PropTypes.any,

    /**
     * The selected item to be selected & shown by default on the autocomplete
     */
    defaultSelectedItem: PropTypes.any,

    /**
     * In case the array of items is not an array of strings,
     * this function is used on each item to return the string that will be shown on the filter
     */
    itemToString: PropTypes.func.isRequired,

    /**
     * Function that generates unique key for each item to highlight selected one
     */
    itemKey: PropTypes.func.isRequired,

    /**
     * Function that will render the 'filter' component.
     */
    children: PropTypes.func.isRequired,

    /**
     * The height of each item in the list
     * Because the list is virtualized this is required beforehand.
     */
    itemSize: PropTypes.number,

    /**
     * Function that returns a component to render the item
     */
    renderItem: PropTypes.func,

    /**
     * The position of the Popover the Autocomplete is rendered in.
     */
    position: PropTypes.oneOf(Object.keys(Position)),

    /**
     * A function that is used to filter the items.
     * It should return a subset of the initial items.
     * By default the "fuzzaldrin-plus" package is used.
     */
    itemsFilter: PropTypes.func,

    /**
     * Prop that enables and disables filtering
     * True: Enables Filtering
     * False: Disables Filtering
     */
    isFilterDisabled: PropTypes.bool,

    /**
     * Defines the minimum height the results container will be
     */
    popoverMinWidth: PropTypes.number,

    /**
     * Defines the maximum height the results container will be
     */
    popoverMaxHeight: PropTypes.number,

    ...Downshift.propTypes
  }

  state = {
    inputValue: '',
    scrollToIndex: 0,
    targetWidth: 0
  }

  static defaultProps = {
    itemToString: i => (i ? String(i) : ''),
    itemSize: 32,
    itemsFilter: fuzzyFilter,
    isFilterDisabled: false,
    popoverMinWidth: 240,
    popoverMaxHeight: 240,
    renderItem: autocompleteItemRenderer
  }

  componentDidMount() {
    this.setState({
      targetWidth: this.targetRef.getBoundingClientRect().width
    })
  }

  renderResults = ({
    width,
    inputValue,
    items,
    highlightedIndex,
    scrollToIndex,
    selectItemAtIndex,
    selectedItem,
    getItemProps
  }) => {
    const {
      title,
      itemKey,
      itemSize,
      itemToString,
      renderItem,
      popoverMaxHeight
    } = this.props

    if (items.length === 0) return null

    // Get item index excluding groups, so that keyboard navigation ignores group rows
    const getActualIndex = itemIndex => {
      let index = 0

      for (let i = 0; i < itemIndex; i++) {
        if (!items[i][GROUP_ITEM_TYPE]) {
          index++
        }
      }

      return index
    }

    return (
      <Pane width={width}>
        {title && (
          <Pane padding={8} borderBottom="muted">
            <Heading size={100}>{title}</Heading>
          </Pane>
        )}
        {items.length > 0 && (
          <VirtualList
            width="100%"
            height={Math.min(items.length * itemSize, popoverMaxHeight)}
            itemSize={itemSize}
            itemCount={items.length}
            scrollToIndex={scrollToIndex}
            overscanCount={3}
            scrollToAlignment="auto"
            renderItem={({ index, style }) => {
              const item = items[index]

              if (item[GROUP_ITEM_TYPE]) {
                return (
                  <Pane
                    key={`group-${item.label}`}
                    style={style}
                    padding={majorScale(1)}
                    borderBottom="muted"
                  >
                    <Heading
                      size={100}
                      whiteSpace="nowrap"
                      textOverflow="ellipsis"
                      overflow="hidden"
                      title={item.label}
                    >
                      {item.label}
                    </Heading>
                  </Pane>
                )
              }

              const itemString = itemToString(item)
              return renderItem(
                getItemProps({
                  item,
                  key: itemString,
                  index: getActualIndex(index),
                  style,
                  children: itemString,
                  onMouseUp: () => {
                    selectItemAtIndex(getActualIndex(index))
                  },
                  isSelected:
                    selectedItem && itemKey(selectedItem) === itemKey(item),
                  isHighlighted: highlightedIndex === getActualIndex(index)
                })
              )
            }}
          />
        )}
      </Pane>
    )
  }

  render() {
    const { inputValue, scrollToIndex } = this.state
    const {
      isFilterDisabled,
      children,
      itemSize,
      position,
      renderItem,
      itemsFilter,
      items: groups,
      popoverMaxHeight,
      popoverMinWidth,
      defaultSelectedItem,
      ...props
    } = this.props

    const items = []
    let itemCount = 0

    for (const group of groups) {
      const groupItems =
        isFilterDisabled || inputValue.trim() === ''
          ? group.items
          : itemsFilter(group.items, inputValue)

      if (groupItems.length > 0) {
        items.push({
          [GROUP_ITEM_TYPE]: true,
          label: group.label
        })

        items.push(...groupItems)
        itemCount += groupItems.length
      }
    }

    return (
      <Downshift
        defaultSelectedItem={defaultSelectedItem}
        {...props}
        itemCount={itemCount}
        onStateChange={this.handleStateChange}
      >
        {({
          isOpen: isShown,
          inputValue,
          getItemProps,
          selectedItem,
          highlightedIndex,
          selectItemAtIndex,
          getRootProps,
          ...restDownshiftProps
        }) => (
          <Pane width="100%" {...getRootProps({ refKey: 'innerRef' })}>
            <Popover
              bringFocusInside={false}
              isShown={isShown}
              minWidth={popoverMinWidth}
              position={
                position ||
                (this.state.targetWidth < popoverMinWidth
                  ? Position.BOTTOM_LEFT
                  : Position.BOTTOM)
              }
              content={() => {
                return this.renderResults({
                  width: Math.max(this.state.targetWidth, popoverMinWidth),
                  inputValue,
                  items,
                  getItemProps,
                  selectedItem,
                  highlightedIndex,
                  scrollToIndex,
                  selectItemAtIndex
                })
              }}
              minHeight={0}
              animationDuration={0}
            >
              {({ isShown: isShownPopover, toggle, getRef }) =>
                children({
                  isShown: isShownPopover,
                  toggle,
                  getRef: ref => {
                    // Use the ref internally to determine the width
                    this.targetRef = ref
                    getRef(ref)
                  },
                  inputValue,
                  selectedItem,
                  highlightedIndex,
                  selectItemAtIndex,
                  ...restDownshiftProps
                })
              }
            </Popover>
          </Pane>
        )}
      </Downshift>
    )
  }

  handleStateChange = changes => {
    if (
      changes.type === Downshift.stateChangeTypes.keyDownArrowUp ||
      changes.type === Downshift.stateChangeTypes.keyDownArrowDown
    ) {
      this.setState({
        scrollToIndex: changes.highlightedIndex
      })
    }

    if (changes.type === Downshift.stateChangeTypes.changeInput) {
      this.setState({
        inputValue: changes.inputValue || '',
        scrollToIndex: 0
      })

      if (this.props.onChangeInputValue) {
        this.props.onChangeInputValue()
      }
    }
  }
}
